"use client";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { SearchedFunds } from "@/type/schemeType";
import icon from "../icons/Icons";
import { apiPost } from "@/utils/apiClient";
import CustomModal from "./CustomModal";
import { ROOT_URL } from "@/utils/gloableVariables";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (fund: SearchedFunds) => void;
};

export default function FundSearchModal({ isOpen, onClose, onSelect }: Props) {
  const [search, setSearch] = useState<string | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [funds, setFunds] = useState<SearchedFunds[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  // Auto focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Debounce input
  useEffect(() => {
    if (!search || search.replace(/\s/g, "").length < 3) {
      setDebouncedSearch("");
      return;
    }

    const timeout = setTimeout(() => {
      setDebouncedSearch(search.trim().toLowerCase());
    }, 300);

    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    if (!debouncedSearch || debouncedSearch.length < 3) return;

    const fetchFunds = async () => {
      setLoading(true);

      try {
        const response = await apiPost("/api/searchScheme", { search: debouncedSearch }, { base: "none" });

        if (!response && response.length <= 0) throw new Error("Failed to fetch funds");

        setFunds(response.data || []);
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("Unknown error occurred");
        }
      } finally {
        setLoading(false);
      }
    };

    const delayDebounce = setTimeout(() => {
      fetchFunds();
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [debouncedSearch]);

  const filteredFunds = useMemo(() => {
    if (!debouncedSearch) return [];
    return funds.filter((fund) => fund.scheme_name.toLowerCase().includes(debouncedSearch));
  }, [debouncedSearch, funds]);

  const clearSearch = useCallback(() => {
    setSearch("");
  }, []);

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      isBlurOverlay
      showCloseButton={false}
      maxSize="w-full xs:max-w-500 max-h-[70vh] xs:max-h-[unset]"
      className="bg-white h-96 rounded-t-5xl xs:rounded-xl p-5 sm:p-6 shadow-lg overflow-y-auto scrollbar-none xs:overflow-hidden"
    >
      <div className="flex justify-between items-center mb-4">
        <p className="text-lg font-semibold">Search Fund</p>

        <button onClick={onClose} className="w-5 h-5">
          <icon.popupCloseIcon className="text-primaryBlack hover:text-gray-600 rotate-45" />
        </button>
      </div>

      <div className="relative px-2 py-2.5 flex items-center gap-2 xs:p-2.75 border bg-[#f9f9f9] sm:bg-white transition duration-350 border-[#d9d9d9] mb-2.5 rounded-xl sm:hover:shadow-[0px_6px_10px_2px_#0000000D]">
        <label htmlFor="search-fund" className={`w-5 h-5`}>
          <icon.searchIcon />
        </label>
        <input
          ref={inputRef}
          type="text"
          id="search-fund"
          value={search || ""}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search for Easy Mutual funds..."
          className="w-[90%] bg-transparent border-none outline-hidden font-normal text-base text-primaryBlack xs:text-sm transition duration-350"
        />
        {search && (
          <button onClick={clearSearch} className="absolute right-5 rounded-full shrink-0 w-3 h-3">
            <icon.popupCloseIcon className="text-[#7B7B7B] hover:text-primaryBlue rotate-45" />
          </button>
        )}
      </div>

      <div className="max-h-60 overflow-y-auto space-y-1.5 pr-1 scrollbar-sm">
        {loading ? (
          <p className="text-sm text-gray-600">Loading funds...</p>
        ) : error ? (
          <p className="text-sm text-red-500">Error: {error}</p>
        ) : filteredFunds.length === 0 ? (
          <div>
            {search && search?.length > 3 ? (
              <p className="text-sm text-center text-gray-600">No matching funds found.</p>
            ) : (
              <p className="text-sm text-gray-600">Type at least three letters to search</p>
            )}
          </div>
        ) : (
          filteredFunds.map((fund, idx) => (
            <button
              key={idx}
              onClick={() => {
                onSelect(fund);
                onClose();
              }}
              className="w-full text-left p-2 transition duration-350 text-primaryBlue bg-[#f7f9ff] sm:bg-white sm:text-primaryBlack sm:hover:text-primaryBlue sm:hover:bg-[#e6ebfd] rounded-lg sm:rounded-xl flex gap-3 items-center"
            >
              <Image
                src={`${ROOT_URL}/img/AMCsLogo/${fund.amc_code}.png`}
                alt={fund.scheme_name || ""}
                width={30}
                height={30}
                className="h-6 w-6 rounded-full shrink-0 object-contain"
                onError={(e) =>
                  ((e.target as HTMLImageElement).src = "https://www.mysiponline.com/img/AMCsLogo/default.png")
                }
              />
              <p className="text-sm font-medium whitespace-nowrap line-clamp-1">{fund.scheme_name}</p>
            </button>
          ))
        )}
      </div>
    </CustomModal>
  );
}
